import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../../helpers/request'
import i from '../../../translations/i'

export type DomainStatus = 'pending_dns' | 'dns_timeout' | 'dns_ok' | 'issuing' | 'active' | 'error' | 'removing'

export interface DnsRecord {
    type: 'A' | 'CNAME'
    host: string
    value: string
    required: boolean
    // true/false — по результату проверки DNS, null — домен ещё не проверялся
    ok: boolean | null
    // Для CNAME: вместо него подойдёт A-запись на IP сервера
    alternative?: 'A'
}

export interface DomainItem {
    id: number
    domain: string
    unicode: string
    url: string
    status: DomainStatus
    source: 'managed' | 'legacy'
    is_primary: boolean
    www_included: boolean
    error: { code: string, message: string | null } | null
    dns: {
        problem: string | null
        found: { a: string[], aaaa: string[], cname: string[] }
        records: DnsRecord[]
        checked_at: string | null
        next_check_at: string | null
        wait_since: string | null
    }
    issuing_since: string | null
    cert: { issued_at: string | null, expires_at: string, days_left: number } | null
    created_at: string | null
}

export interface DomainSettings {
    enabled: boolean
    ipv4: string
    cname: string
    max: number
    wait_hours: number
}

// Домены в этих статусах меняются сами (DNS обновился, выпущен сертификат, домен снят): их опрашиваем
const TRANSITIONAL: DomainStatus[] = ['pending_dns', 'issuing', 'removing']

const POLL_MS = 4000

class DomainController {
    constructor() {
        makeAutoObservable(this, { timer: false, polling: false } as any)
    }

    loaded = false
    items: DomainItem[] = []
    settings: DomainSettings = { enabled: true, ipv4: '', cname: '', max: 3, wait_hours: 72 }

    adding = false
    // Код ошибки добавления (domain_invalid, domain_taken, …): текст по коду берёт экран
    addError: string | null = null
    checking: Record<number, boolean> = {}
    // Выпуск запускается кнопкой: пока сервер перепроверяет DNS и ставит задание, кнопка крутится
    issuing: Record<number, boolean> = {}
    // Домен, который только что заработал: карточка на несколько секунд подсвечивается
    celebrate: number | null = null

    private timer: any = null
    private polling = false

    get hasTransitional() {
        return this.items.some(item => TRANSITIONAL.includes(item.status))
    }

    get canAdd() {
        return this.settings.enabled && this.items.filter(item => item.status !== 'removing').length < this.settings.max
    }

    get activeCount() {
        return this.items.filter(item => item.status === 'active').length
    }

    private apply(data: any) {
        const before = new Map(this.items.map(item => [item.id, item.status]))
        const items: DomainItem[] = data.items

        items.forEach(item => {
            const was = before.get(item.id)
            if (item.status === 'active' && was && was !== 'active' && was !== 'removing') {
                this.celebrate = item.id
                setTimeout(() => runInAction(() => {
                    if (this.celebrate === item.id) this.celebrate = null
                }), 8000)

                notification.success({
                    message: i.t('domain:ready.notify'),
                    description: i.t('domain:ready.notifyText', { domain: item.unicode }),
                })
            }
        })

        this.settings = {
            enabled: data.enabled,
            ipv4: data.ipv4,
            cname: data.cname,
            max: data.max,
            wait_hours: data.wait_hours,
        }
        this.items = items
        this.loaded = true
    }

    get(silent = false) {
        return openApi('get', 'domains', {}, undefined, silent)
            .then(result => runInAction(() => this.apply(result.data)))
    }

    startPolling() {
        this.polling = true
        this.schedule()
    }

    stopPolling() {
        this.polling = false
        clearTimeout(this.timer)
    }

    private schedule() {
        clearTimeout(this.timer)
        if (!this.polling) return

        this.timer = setTimeout(async () => {
            if (this.hasTransitional && !document.hidden) {
                await this.get(true).catch(() => undefined)
            }
            this.schedule()
        }, POLL_MS)
    }

    private replace(item: DomainItem) {
        const index = this.items.findIndex(el => el.id === item.id)
        if (index >= 0) this.items[index] = item
        else this.items.push(item)
    }

    async add(raw: string) {
        this.adding = true
        this.addError = null

        try {
            const result = await openApi('post', 'domains', { domain: raw })
            runInAction(() => this.replace(result.data))

            return true
        } catch (error: any) {
            runInAction(() => {
                this.addError = error?.response?.data?.code ?? 'unknown'
            })

            return false
        } finally {
            runInAction(() => {
                this.adding = false
            })
        }
    }

    async check(id: number) {
        this.checking[id] = true

        try {
            const result = await openApi('post', `domains/${id}/check`, {}, undefined, true)
            runInAction(() => this.replace(result.data))
        } catch (error) {
            console.log(error)
        } finally {
            runInAction(() => {
                this.checking[id] = false
            })
        }
    }

    // Кнопка «Выпустить сертификат» (и «Повторить» после ошибки)
    async issue(id: number) {
        this.issuing[id] = true

        try {
            const result = await openApi('post', `domains/${id}/issue`, {}, undefined, true)
            runInAction(() => this.replace(result.data))
        } catch (error) {
            console.log(error)
        } finally {
            runInAction(() => {
                this.issuing[id] = false
            })
        }
    }

    async makePrimary(id: number) {
        try {
            await openApi('post', `domains/${id}/primary`)
            await this.get(true)
        } catch (error) {
            console.log(error)
        }
    }

    async remove(id: number) {
        try {
            const result = await openApi('delete', `domains/${id}`)
            runInAction(() => {
                if (result.data.deleted) this.items = this.items.filter(item => item.id !== id)
                else this.replace(result.data)
            })
        } catch (error) {
            console.log(error)
        }
    }
}

export default new DomainController()
